import { createContext } from 'react';
import { ObservationHistory } from '@iotv/datamodel-core';
import { isNonNaNNumber, isReading, objectsMatch } from '@iotv/iotv-v3-types';
import { ducktypeStringMap } from '@iotv/core-services';
import { filterKeysByTypeToViewDefinition } from '../helperFns';
import { makeObservationHistoryPeriodBased, observationHistoryIsPeriodBased, ObservationInputMetaKeys } from '../fns';
const debug = process.env.REACT_APP_DEBUG && false;
export const isPointInTimeObservation = isReading;
export const isPeriodObservation = (obj) => {
    return isReading(obj) && typeof obj.fromTimestampMs === 'number' && typeof obj.toTimestampMs === 'number';
};
const isSetObservationHistoryAction = (action) => action.type === 'setObservationHistory';
const isDeleteObservationAction = (action) => action.type === 'deleteObservation';
const isAddObservationAction = (action) => action.type === 'addObservation';
const isEditObservationAction = (action) => action.type === 'editObservation';
const isSetNewObservationAction = (action) => action.type === 'setNewObservation';
const isRequestDeleteKeyAction = (action) => action.type === 'requestDeleteKeys';
const isAddKeysAction = (action) => action.type === 'addKeys';
const isRemoveKeysAction = (action) => action.type === 'removeKeys';
const isSetObjectHistoryAction = (action) => action.type === 'setObjectHistory';
const isSetTimePeriodModeAction = (action) => action.type === 'setTimePeriodMode';
const isSetBucketingStrategyAction = (action) => action.type === 'setBucketingStrategy';
const isSetMatchedVideoHubContextsAction = (action) => action.type === 'setMatchedVideoHubContexts';
export const DefaultState = {
    isPeriodBased: true,
    observationHistory: null,
    viewDefinition: null,
    objectHistory: null,
    unusedKeys: null,
    selectedKeys: null,
    requestedDeleteKeys: null,
    matchedActualsCount: null,
    googleSheet: null,
    newObservation: null,
    bucketStrategyMap: null,
    matchedVideoHubContexts: null,
};
const ducktypeWrapper = (ob) => {
    const ducktyped = ducktypeStringMap(ob);
    debug && console.log(`Ducktyping`, ob, ducktyped);
    return ducktyped;
};
export const ObservationsReducer = (state, action) => {
    debug && console.log('ObservationReducer got action ', action);
    const updatedState = { ...state };
    const { observationHistory, newObservation } = updatedState;
    if (isSetObservationHistoryAction(action) && action.value) {
        const observationHistory = action.value;
        const knownKeys = observationHistory.observedHistory.keys();
        return {
            ...state,
            observationHistory,
            observationHistoryStore: observationHistory.observedHistory,
            knownKeys,
        };
    }
    else if (action.type === 'reset') {
        return DefaultState;
    }
    else if (isDeleteObservationAction(action) && action.value) {
        const { value: { idx }, } = action;
        if (observationHistory) {
            const splicedHistoryStore = observationHistory.observedHistory.clone().splice(idx, 1);
            updatedState.observationHistory = new ObservationHistory({ ...observationHistory, observedHistory: splicedHistoryStore });
        }
        else {
            debug && console.log('Did not delete observation ', { idx });
        }
    }
    else if (isAddObservationAction(action)) {
        const { value } = action;
        if (observationHistory && isNonNaNNumber(value.timestamp)) {
            const { timestamp, description, fromTimestampMs, toTimestampMs, observationType, periodBasisMs = null, state, } = value;
            const transformedState = { description, fromTimestampMs, toTimestampMs, observationType, periodBasisMs, ...state };
            const stateSnapshot = {
                state: ducktypeWrapper(transformedState),
                timestamp,
            };
            const newObservationHistory = new ObservationHistory({
                ...observationHistory,
                observedHistory: observationHistory.observedHistory.clone().push(stateSnapshot),
            });
            updatedState.observationHistory = newObservationHistory;
            updatedState.newObservation = null;
        }
        else {
            debug && console.log('Did not add observation ', value);
        }
    }
    else if (isSetNewObservationAction(action)) {
        updatedState.newObservation = action.value ?? null;
    }
    else if (isEditObservationAction(action)) {
        const { value: { idx, newRow, oldRow }, } = action;
        debug && console.log('processRowUpdate edit ', { idx, newRow: newRow, oldRow: oldRow });
        if (observationHistory && observationHistory.observedHistory) {
            const observationHistoryStore = observationHistory.observedHistory;
            const idx = observationHistoryStore.timestamp.findIndex((t) => newRow.timestamp === t);
            if (idx !== -1) {
                const { timestamp, localTimeStr, sk, id, pk, type, ..._stateObject } = newRow;
                debug && console.log('EditObservationAction processRowUpdate modify', { timestamp, _stateObject });
                observationHistoryStore.modifyByTimestamp(timestamp, _stateObject);
                const observationHistoryClone = new ObservationHistory({ ...observationHistory, observedHistory: observationHistoryStore });
                updatedState.observationHistory = observationHistoryClone;
                debug && console.log('EditObservationAction processRowUpdate spliced', observationHistoryClone);
            }
            else {
                debug && console.log('EditObservationAction processRowUpdate no idx for', newRow);
            }
        }
    }
    else if (isRequestDeleteKeyAction(action)) {
        const { value: keysToDelete } = action;
        updatedState.requestedDeleteKeys = keysToDelete;
    }
    else if (isAddKeysAction(action) && action.value) {
        const keysToAdd = action.value;
        if (observationHistory) {
            for (const key of keysToAdd) {
                observationHistory.observedHistory.addKey(key);
            }
            updatedState.observationHistory = new ObservationHistory({ ...observationHistory, observedHistory: observationHistory.observedHistory });
        }
        else {
            debug && console.log('Did not add keys as no ObservationHistory', keysToAdd);
        }
    }
    else if (isRemoveKeysAction(action) && action.value) {
        const keysToRemove = action.value;
        if (observationHistory) {
            for (const key of keysToRemove) {
                observationHistory.observedHistory.removeKey(key);
            }
            updatedState.observationHistory = new ObservationHistory({ ...observationHistory, observedHistory: observationHistory.observedHistory });
        }
        else {
            debug && console.log('Did not remove keys as no ObservationHistory', keysToRemove);
        }
    }
    else if (isSetObjectHistoryAction(action)) {
        const { value: objectHistory } = action;
        const recentStateHistory = objectHistory.recentStateHistory;
        if (recentStateHistory) {
            updatedState.objectHistory = objectHistory;
        }
    }
    else if (isSetTimePeriodModeAction(action)) {
        if (observationHistory) {
            updatedState.isPeriodBased = action.value;
        }
    }
    else if (isSetBucketingStrategyAction(action)) {
        const { value: bucketStrategyMap } = action;
        if (updatedState.bucketStrategyMap) {
            updatedState.bucketStrategyMap = { ...updatedState.bucketStrategyMap, ...bucketStrategyMap };
        }
        else {
            updatedState.bucketStrategyMap = { ...bucketStrategyMap };
        }
    }
    else if (isSetMatchedVideoHubContextsAction(action)) {
        const { value: videoHubContexts } = action;
        updatedState.matchedVideoHubContexts = {};
        for (const videoHubContext of videoHubContexts) {
            const videoStreams = videoHubContext.getNodesOfType('VideoStream');
            const videoStream = videoStreams[0];
            if (videoStream) {
                const sk = videoStream.sk;
                updatedState.matchedVideoHubContexts[sk] = videoHubContext;
            }
        }
    }
    if (updatedState.observationHistory?.observedHistory) {
        updatedState.viewDefinition = filterKeysByTypeToViewDefinition(updatedState.observationHistory.observedHistory.keys(), updatedState.observationHistory.observedHistory);
    }
    const usedKyes = new Set(observationHistory?.observedHistory?.keys());
    const knownKeys = updatedState.objectHistory?.recentStateHistory?.keys() ?? [];
    const unusedKeys = knownKeys.filter((k) => !usedKyes.has(k)) ?? null;
    updatedState.unusedKeys = unusedKeys;
    updatedState.selectedKeys = observationHistory?.observedHistory?.keys().filter((k) => !ObservationInputMetaKeys.includes(k)) ?? null;
    if (updatedState.isPeriodBased && updatedState.observationHistory && !observationHistoryIsPeriodBased(updatedState.observationHistory)) {
        debug && console.log(`INFO converting to period based`);
        updatedState.observationHistory = makeObservationHistoryPeriodBased(updatedState.observationHistory);
    }
    else {
        debug && console.log(`INFO not converting to period based`, { isPeriodBased: updatedState.isPeriodBased, observationHistory, observationHistoryIsPeriodBased: observationHistory ? observationHistoryIsPeriodBased(observationHistory) : null });
    }
    const statesChanged = !(objectsMatch(state, updatedState) && objectsMatch(state, updatedState));
    return statesChanged ? updatedState : state;
};
export const ObservationsContext = createContext({
    state: DefaultState,
    dispatch: () => null,
});
